import { z } from 'zod';
import {
  messageChannelSchema,
  objectIdSchema,
  orderAvailableMessagesSchema,
  phoneSchema,
} from '@hero/schema';
import { API } from '..';

/**
 * Send a transactional message for an order (WhatsApp waybill and driver
 * assign today; email later via `channel`).
 *
 * N phones → N Conversations (get-or-create per phone on the system
 * WhatsApp channel) → one ConversationMessage each. See
 * `docs/notifications.md` § Transcript mirror.
 */
export const messageSendAPISchema = z.object({
  orderId: objectIdSchema.describe(
    'Order to send messaging for. Resolve from search_orders / get_order.',
  ),
  channel: messageChannelSchema
    .default('whatsapp')
    .describe('Delivery channel. Only `whatsapp` is implemented today; `email` is reserved.'),
  templateId: orderAvailableMessagesSchema
    .default('order_waybill')
    .describe(
      'Template to send. `order_waybill` → order owner; `order_driver_assign` → assigned driver.',
    ),
  to: z
    .object({
      phones: z
        .array(phoneSchema)
        .min(1)
        .max(50)
        .describe('E.164 phones to send to. Duplicates are removed.'),
    })
    .optional()
    .describe(
      'Explicit recipients. Driver phone → `order_driver_assign`; all other phones → `order_waybill`. When omitted, `templateId` picks the default single recipient.',
    ),
});

export type MessageSendAPI = API<
  z.infer<typeof messageSendAPISchema>,
  {
    sent: number;
    failed: { phone: string; reason: string }[];
  }
>;
